<template>
  <div class="content">
    <h1>Notifications</h1>

    <b-alert
      v-if="alert.message"
      :variant="alert.variant"
      show
      dismissible
      @dismissed="alert.message = ''"
    >
      {{ alert.message }}
    </b-alert>

    <div class="notification-settings shadow-sm">
      <div class="setting-row">
        <div class="setting-label">
          <strong>Daily Reminder</strong>
          <p class="text-muted mb-0">
            Get a daily notification to check in with your program.
          </p>
        </div>
        <b-form-checkbox
          v-model="enabled"
          switch
          size="lg"
        />
      </div>

      <transition name="fade">
        <div
          v-if="enabled"
          class="setting-row"
        >
          <label
            for="reminder-time"
            class="setting-label"
          >
            <strong>Reminder Time</strong>
          </label>
          <input
            id="reminder-time"
            v-model="time"
            type="time"
            class="time-input form-control"
          >
        </div>
      </transition>
    </div>

    <b-button
      variant="primary"
      size="lg"
      class="mt-4"
      :disabled="saving"
      @click="save"
    >
      <template v-if="saving">
        Saving <spinner />
      </template>
      <template v-else>
        Save
      </template>
    </b-button>
  </div>
</template>

<script>
import { LocalNotifications } from '@capacitor/local-notifications';
import Spinner from '@/components/Spinner.vue';

const NOTIFICATION_ID = 1001;

export default {
  name: 'Notifications',
  components: { Spinner },
  data() {
    const prefs = this.$store.state.notificationPreferences;
    return {
      enabled: prefs.enabled,
      time: prefs.time,
      saving: false,
      alert: { message: '', variant: 'success' },
    };
  },
  methods: {
    async save() {
      this.saving = true;
      this.alert.message = '';

      try {
        // Cancel any existing scheduled notification first
        await LocalNotifications.cancel({ notifications: [{ id: NOTIFICATION_ID }] });

        if (this.enabled) {
          const { display } = await LocalNotifications.requestPermissions();
          if (display !== 'granted') {
            this.alert = { message: 'Permission denied. Please enable notifications in your device settings.', variant: 'danger' };
            this.saving = false;
            return;
          }

          const [hourStr, minuteStr] = this.time.split(':');
          await LocalNotifications.schedule({
            notifications: [{
              title: 'Daily Journal Reminder',
              body: "It's time for your daily check-in!",
              id: NOTIFICATION_ID,
              schedule: {
                on: { hour: parseInt(hourStr, 10), minute: parseInt(minuteStr, 10) },
                repeats: true,
              },
            }],
          });
        }

        this.$store.commit('notificationPreferences', { enabled: this.enabled, time: this.time });
        this.alert = { message: 'Settings saved.', variant: 'success' };
      } catch (e) {
        this.alert = { message: 'Something went wrong. Please try again.', variant: 'danger' };
      }

      this.saving = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.notification-settings {
  background: #fff;
  border-radius: 8px;
  border: 1px solid #dee2e6;
  overflow: hidden;
}

.setting-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;

  & + & {
    border-top: 1px solid #dee2e6;
  }
}

.setting-label {
  margin-bottom: 0;
  margin-right: 1rem;
}

.time-input {
  width: auto;
  min-width: 130px;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
